import { QueryClient } from '@tanstack/react-query';
import { paymentLinkNamespace } from 'receivable-invoices-js/constants/hosts';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import { paymentLinkQueries } from './fetch-payment-link';

vi.mock('receivable-invoices-js/react/utils/api-clients/qonto');
vi.mock('receivable-invoices-js/constants/hosts', () => ({
  paymentLinkNamespace: '/api/v5/payment_links',
}));

vi.mock('@repo/design-system-kit', () => ({
  useToast: vi.fn(() => ({
    showToast: vi.fn(),
  })),
}));

describe('unit | API | queries | fetch-payment-link', () => {
  const mockQontoClient = vi.mocked(qontoClient);
  const resourceId = 'invoice-123';
  const paymentLinkId = 'payment-link-456';
  const URL = `${paymentLinkNamespace}/payment_links/checkout`;

  beforeEach(() => {
    vi.clearAllMocks();
  });

  describe('paymentLinkQueries.findItem', () => {
    it('fetches payment link by resource ID successfully', async () => {
      const apiResponse = {
        data: {
          id: paymentLinkId,
          type: 'payment_links',
          attributes: {
            amount: {
              currency: 'EUR',
              value: '100.00',
            },
            latest_payment_method: 'credit_card',
            debitor_name: 'John Doe',
            payment_initiator: {
              resource_id: resourceId,
              resource_type: 'Invoice',
              resource_number: 'INV-001',
            },
            status: 'open',
            potential_payment_methods: ['credit_card', 'bank_transfer'],
            methods: ['credit_card', 'bank_transfer'],
            settings: {
              redirect_url: 'https://example.com/success',
              reusable: false,
            },
            expiration_date: '2025-12-31',
            organization_name: 'Test Org',
            name: 'Test Payment',
            items: [],
            profile_id: 'profile-123',
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const queryClient = new QueryClient();
      const queryOptions = paymentLinkQueries.findItem({ resourceId });
      const context = {
        client: queryClient,
        queryKey: queryOptions.queryKey,
        signal: new AbortController().signal,
        meta: undefined,
      };
      const result = await queryOptions.queryFn?.(context);

      expect(mockQontoClient).toHaveBeenCalledWith(URL, {
        params: { resource_id: resourceId },
      });

      expect(result).toStrictEqual(
        camelizeKeys({
          ...apiResponse.data.attributes,
          id: paymentLinkId,
        })
      );
    });

    it('caches result by payment link ID', async () => {
      const apiResponse = {
        data: {
          id: paymentLinkId,
          type: 'payment_links',
          attributes: {
            amount: { currency: 'EUR', value: '100.00' },
            status: 'open',
            potential_payment_methods: ['credit_card'],
            expiration_date: '2025-12-31',
            name: 'Test Payment',
            profile_id: 'profile-123',
            payment_initiator: {
              resource_id: resourceId,
              resource_type: 'Invoice',
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const queryClient = new QueryClient();
      const setQueryDataSpy = vi.spyOn(queryClient, 'setQueryData');

      const queryOptions = paymentLinkQueries.findItem({ resourceId });
      const context = {
        client: queryClient,
        queryKey: queryOptions.queryKey,
        signal: new AbortController().signal,
        meta: undefined,
      };
      await queryOptions.queryFn?.(context);

      expect(setQueryDataSpy).toHaveBeenCalledWith(
        paymentLinkQueries.item(paymentLinkId).queryKey,
        expect.any(Object)
      );
    });
  });

  describe('paymentLinkQueries.item', () => {
    it('fetches payment link by ID successfully', async () => {
      const apiResponse = {
        data: {
          id: paymentLinkId,
          type: 'payment_links',
          attributes: {
            amount: {
              currency: 'EUR',
              value: '100.00',
            },
            status: 'paid',
            potential_payment_methods: ['credit_card'],
            expiration_date: '2025-12-31',
            name: 'Test Payment',
            profile_id: 'profile-123',
            payment_initiator: {
              resource_id: resourceId,
              resource_type: 'Invoice',
            },
          },
        },
      };

      mockQontoClient.mockResolvedValue(apiResponse);

      const queryClient = new QueryClient();
      const queryOptions = paymentLinkQueries.item(paymentLinkId);
      const context = {
        client: queryClient,
        queryKey: queryOptions.queryKey,
        signal: new AbortController().signal,
        meta: undefined,
      };
      const result = await queryOptions.queryFn?.(context);

      expect(mockQontoClient).toHaveBeenCalledWith(`${URL}/${paymentLinkId}`);
      expect(result).toStrictEqual(
        camelizeKeys({
          ...apiResponse.data.attributes,
          id: paymentLinkId,
        })
      );
    });
  });

  describe('error handling', () => {
    it('throws error when API call fails', async () => {
      const error = new Error('API Error');
      mockQontoClient.mockRejectedValue(error);

      const queryClient = new QueryClient();
      const queryOptions = paymentLinkQueries.item(paymentLinkId);
      const context = {
        client: queryClient,
        queryKey: queryOptions.queryKey,
        signal: new AbortController().signal,
        meta: undefined,
      };

      await expect(queryOptions.queryFn?.(context)).rejects.toThrow('API Error');
    });
  });
});
